import AirDatepicker, {
  type AirDatepickerLocale,
  type AirDatepickerOptions,
  type AirDatepickerPosition,
} from 'air-datepicker';

// React & Hooks
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CellComponent, RowComponent, Tabulator } from 'tabulator-tables';
import useBoxManagement from '@/domains/asset-management/hooks/useBoxManagement';
import useFacilityManagement from '@/domains/asset-management/hooks/useFacilityManagement';
// Types
import type {
  ButtonState,
  CreateFacilityPayload,
  FacilityDetailData,
  FacilityFormValues,
  UpdateFacilityPayload,
} from '@/domains/asset-management/types/facility';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import ButtonGroup from '@/shared/components/ui/ButtonGroup/ButtonGroup';
import ComboInput from '@/shared/components/ui/ComboInput/ComboInput';
// UI Components
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
import GeneralInput from '@/shared/components/ui/GeneralInput/GeneralInput';
import { ReactTabulator } from '@/shared/components/ui/LocalizedTabulator/LocalizedTabulator';
import LogList from '@/shared/components/ui/LogList/LogList';
import Select from '@/shared/components/ui/Select/Select';
import TablePagination from '@/shared/components/ui/TablePagination/TablePagination';
// Custom Hooks
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import { remoteSortHandler, useServerTable } from '@/shared/hooks/useServerTable';
import useUnmappedSiteRoad from '@/shared/hooks/useUnmappedSiteRoad';
import { formatFullDateTimeDDMMYYYY } from '@/shared/utils/date';

// External Libraries & Styles
import 'air-datepicker/air-datepicker.css';
import localeEn from 'air-datepicker/locale/en';
import localeId from 'air-datepicker/locale/id';
import localeKo from 'air-datepicker/locale/ko';

// Utilities
import dayjs from 'dayjs';

const commonColumnSettings = {
  hozAlign: 'center' as const,
  headerHozAlign: 'center' as const,
  resizable: false,
};

// Columns whose `field` matches the backend allowed-sort whitelist keys.
// facility endpoint allows: id (facility_id) | updated_time.
const FACILITY_SORT_FIELD_MAP: Record<string, string> = {
  facility_id: 'id',
  updated_time: 'updated_time',
};

const data = [
  {
    facility_id: 'Police Station',
    name: 'Polres Grobogan ',
    s_n: '-7.0853',
    site_type_value: '110.9166 ',
  },
];

const FacilityManagement = () => {
  const { t, i18n } = useTranslation();

  const tbRef = useRef<Tabulator | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const [disabledForm, setDisabledForm] = useState<boolean>(true);
  const [hasChangesUpdate, setHasChangesUpdate] = useState<boolean>(false);
  const [hasChangesCreate, setHasChangesCreate] = useState<boolean>(false);
  const [isNewClicked, setIsNewClicked] = useState<boolean>(false);
  const hasChangesUpdateRef = useRef(hasChangesUpdate);
  const hasChangesCreateRef = useRef(hasChangesCreate);
  const isNewClickedRef = useRef(isNewClicked);
  const [newId, setNewId] = useState<number | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null | string>(null);

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, []);

  //Params
  const [selectedOption, setSelectedOption] = useState<string>('');
  // type=221001 (speaker) is the fixed scope of this page; always part of the query.
  const table = useServerTable<{ type: string; input?: string }>({
    initialFilters: { type: '221001' },
    defaultSort: { field: 'updated_time', dir: 'desc' },
    defaultSize: 10,
  });
  const queryParams = table.queryString;
  const facilityColumns = useMemo(
    () => [
      {
        title: 'No',
        formatter: (cell: CellComponent) =>
          (table.page - 1) * table.size + cell.getRow().getPosition(true),
        width: 60,
        headerSort: false,
        ...commonColumnSettings,
      },
      // TODO: i18n - hardcoded Korean string
      {
        title: '스피커 ID',
        field: 'facility_id',
        headerSort: true,
        ...commonColumnSettings,
      },
      {
        title: '명칭',
        field: 'name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnSettings,
      },
      {
        title: '시리얼 넘버',
        field: 's_n',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnSettings,
      },
      {
        title: '매핑 사이트 타입',
        field: 'site_type_value',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnSettings,
      },
      {
        title: '매핑 사이트',
        field: 'site_name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnSettings,
      },
      {
        title: '매핑 접근로',
        field: 'road_name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnSettings,
      },
      {
        title: '매핑 함체',
        field: 'remote_terminal_unit_id',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnSettings,
      },
      {
        title: '업데이트 일시',
        field: 'updated_time',
        widthGrow: 1,
        headerSort: true,
        ...commonColumnSettings,
      },
    ],
    [table.page, table.size]
  );
  const [optionParams] = useState('upper-code=221');
  const [resource, setResource] = useState('');
  const [selectedFacility, setSelectedFacility] = useState({
    fc_id: null,
  });
  const selectedFacilityRef = useRef(selectedFacility);

  useEffect(() => {
    hasChangesUpdateRef.current = hasChangesUpdate;
    hasChangesCreateRef.current = hasChangesCreate;
    isNewClickedRef.current = isNewClicked;
    selectedFacilityRef.current = selectedFacility;
  }, [hasChangesUpdate, hasChangesCreate, isNewClicked, selectedFacility]);

  const detailFacility = useCallback((data: FacilityDetailData) => {
    return {
      facility_id: data.facility_id || null,
      site_id: data.site_id || 'NO_MAPPING',
      road_id: data.road_id || 'NO_MAPPING',
      remote_terminal_unit_id: data.remote_terminal_unit_id || null,
      name: data.name || null,
      description: data.description || null,
      installed_date: data.installed_date || null,
      pic_name: data.pic_name || null,
      pic_phone_number: data.pic_phone_number || null,
      model: data.model || null,
      s_n: data.s_n || null,
      lat: data.lat || null,
      lng: data.lng || null,
      manufacturer: data.manufacturer || null,
      ip: data.ip || null,
      mac: data.mac || null,
      port: data.port || null,
      endpoint_id: (data as any).endpoint_id || null,
      type: data.type || null,
      config_json: data.config_json || null,
    };
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormValues((prevValues) => ({
        ...prevValues,
        [name]: value,
      }));

      if (name === 'site_id') {
        setSelectedSiteId(value);
      }

      if (isNewClickedRef.current) {
        setHasChangesCreate(true);
        if (name === 'type') {
          if (value === '221001') {
            setResource('resource=speaker');
          } else if (value === '221002') {
            setResource('resource=vms');
          } else {
            setResource('');
          }
        }
      } else {
        setHasChangesUpdate(true);
      }

      if (selectedFacilityRef.current?.fc_id) {
        setHasChangesUpdate(true);
        if (name === 'type') {
          if (value === '221001') {
            setResource(`id=${selectedFacilityRef.current?.fc_id}&resource=speaker`);
          } else if (value === '221002') {
            setResource(`id=${selectedFacilityRef.current?.fc_id}&resource=vms`);
          } else {
            setResource('');
          }
        }
      }
    },
    []
  );

  useEffect(() => {
    let locale: AirDatepickerLocale;
    if (i18n.language === 'eng') {
      locale = localeEn;
    } else if (i18n.language === 'ind') {
      locale = localeId;
    } else {
      locale = localeKo;
    }

    const optionsDate: AirDatepickerOptions<HTMLElement> = {
      autoClose: true,
      locale: locale,
      position: 'top center' as AirDatepickerPosition,
      onSelect: (date) => {
        const selected = Array.isArray(date.formattedDate)
          ? date.formattedDate[0]
          : date.formattedDate;
        handleInputChange({
          target: {
            name: 'installed_date',
            value: dayjs(selected).format('YYYY-MM-DD'),
          },
        } as unknown as React.ChangeEvent<HTMLInputElement>);
        setFormValues((prevValues) => ({
          ...prevValues,
          installed_date: dayjs(selected).format('YYYY-MM-DD'),
        }));
      },
    };

    const datepicker = new AirDatepicker('[name="installed_date"]', optionsDate);

    return () => {
      datepicker.destroy();
    };
  }, [i18n.language, handleInputChange]);

  //Values
  const [formValues, setFormValues] = useState<FacilityFormValues>({
    facility_id: null,
    site_id: null,
    road_id: null,
    remote_terminal_unit_id: null,
    name: null,
    description: null,
    installed_date: null,
    pic_name: null,
    pic_phone_number: null,
    model: null,
    s_n: null,
    lat: null,
    lng: null,
    manufacturer: null,
    ip: null,
    mac: null,
    port: null,
    endpoint_id: null,
    type: null,
    config_json: null,
  });

  const emptyDetail = () => {
    setFormValues((prevValues) => {
      const emptyValues = {} as FacilityFormValues;

      (Object.keys(prevValues) as (keyof FacilityFormValues)[]).forEach((key) => {
        emptyValues[key] = null;
      });
      return emptyValues;
    });
  };

  //Button
  const [buttonState, setButtonState] = useState<ButtonState>({
    confirm: true,
    cancel: true,
    delete: true,
    create: false,
  });

  const disableAllButtons = useCallback(() => {
    setButtonState({
      confirm: true,
      cancel: true,
      delete: true,
      create: true,
    });
  }, []);

  const enableInitialButtons = () => {
    disableAllButtons();
    setIsNewClicked(false);
    setButtonState((prevState) => ({
      ...prevState,
      create: false,
    }));
  };

  const enableUPDATEButtons = useCallback(() => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
      delete: false,
      create: false,
    }));
  }, [disableAllButtons]);

  const enableRegisterButtons = () => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
    }));
  };

  const reloadCallback = () => {
    enableInitialButtons();
    tbRef.current?.deselectRow();
    emptyDetail();
    setDisabledForm(true);
    setHasChangesUpdate(false);
    setIsNewClicked(false);
    setHasChangesCreate(false);
    setSelectedSiteId(null);
    setSelectedFacility({ fc_id: null });
    setNewId(null);
  };

  const {
    facilityListDataManagementPage,
    detailFacilityData,
    facilityEndpointsData,
    deleteFacility,
    createFacility,
    updateFacility,
    refetchDetailFacility,
  } = useFacilityManagement({
    fcID: selectedFacility?.fc_id,
    queryParams: queryParams,
    facilityType: 'speaker',
    onDeleteSuccess: reloadCallback,
    onUpdateSuccess: () => {
      refetchDetailFacility();
      enableUPDATEButtons();
      setIsNewClicked(false);
      setHasChangesUpdate(false);
    },
    onCreateSuccess: (responseData) => {
      reloadCallback();
      const newID = responseData.facility_id;
      setNewId(newID);
    },
  });

  // size=0 → full box list (mapping dropdown source, not a paged table).
  const { boxListData } = useBoxManagement({ queryParams: 'size=0' });

  const pageData = facilityListDataManagementPage?.data;
  const data = pageData?.rows ?? [];
  const dataFacility = detailFacilityData?.data;

  const endpointOptions = (() => {
    const speakers = facilityEndpointsData?.data?.speaker_jdsolution;
    if (!speakers) return [];
    return Object.entries(speakers).map(([id, serialPort]) => ({
      label: `${id} (${serialPort})`,
      value: id,
    }));
  })();

  const { commonListData } = useCommonCodes({ optionParams });
  const { unmappedSiteRoad } = useUnmappedSiteRoad({ resource });
  const dataSiteRoad = unmappedSiteRoad?.data;

  const handleOnChangeInputSelect = useCallback(({ target }: { target: { value: string } }) => {
    const { value } = target;
    setSelectedOption(value);
  }, []);

  useEffect(() => {
    if (selectedSiteId === 'NO_MAPPING') {
      setFormValues((prevValues) => ({
        ...prevValues,
        road_id: 'NO_MAPPING',
        remote_terminal_unit_id: 'NO_MAPPING',
      }));
    }
  }, [selectedSiteId]);

  const languageTabulator = () => {
    const datalanguage = {
      pagination: {
        first: t('cmn > first page'),
        first_title: t('cmn > first page'),
        last: t('cmn > last page'),
        last_title: t('cmn > last page'),
        prev: t('cmn > page before'),
        prev_title: t('cmn > page before'),
        next: t('cmn > next page'),
        next_title: t('cmn > next page'),
      },
    };
    return datalanguage;
  };

  const optionsTabulator = {
    debugInvalidOptions: true,
    // Server-side pagination: Tabulator's own pager is off; <TablePagination> drives it.
    pagination: false,
    rowHeight: 41,
    selectableRows: false,
    movableRows: false,
    resizableRows: false,
    index: 'facility_id',
    locale: 'ko',
    langs: {
      ko: languageTabulator(),
    },
    selectableRowsCheck: (row: RowComponent) => {
      return !row.getElement().classList.contains('tabulator-selected');
    },
    placeholder: "<div style='text-align:center;'> No Data</div>",
  };

  const handleSearch = useCallback(
    (inputVal: string) => {
      table.setFilters({ input: inputVal || undefined });
    },
    [table]
  );

  const handleReset = () => {
    setSelectedOption('All');
    table.reset();
  };

  const handleRowSelected = useCallback(
    (row: RowComponent) => {
      if (hasChangesCreateRef.current || hasChangesUpdateRef.current) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), {
          mode: 'confirm',
        });
        message
          .confirmClicked()
          .then(() => {
            tbRef.current?.deselectRow();
            row.select();
            row.getElement().classList.add('custom-hover');

            const rowData = row.getData();
            setSelectedFacility({
              fc_id: rowData.facility_id,
            });
            setSelectedSiteId(rowData.site_id ?? 'NO_MAPPING');
            if (rowData.type === '221001') {
              setResource(`id=${rowData.facility_id}&resource=speaker`);
            } else if (rowData.type === '221002') {
              setResource(`id=${rowData.facility_id}&resource=vms`);
            }
            setDisabledForm(false);
            setHasChangesUpdate(false);
            setHasChangesCreate(false);
            setIsNewClicked(false);
          })
          .catch(() => {
            row.deselect();
            const prevId = selectedFacilityRef.current?.fc_id;
            if (prevId) {
              const prevRow = tbRef.current?.getRow(prevId) || null;
              prevRow?.select();
              prevRow?.getElement().classList.add('custom-hover');
            }
          });
      } else {
        tbRef.current?.deselectRow();
        row.select();
        row.getElement().classList.add('custom-hover');

        const rowData = row.getData();
        setSelectedFacility({
          fc_id: rowData.facility_id,
        });
        if (rowData.type === '221001') {
          setResource(`id=${rowData.facility_id}&resource=speaker`);
        } else if (rowData.type === '221002') {
          setResource(`id=${rowData.facility_id}&resource=vms`);
        }
        setSelectedSiteId(rowData.site_id ?? 'NO_MAPPING');
        setDisabledForm(false);
        enableUPDATEButtons();
        setIsNewClicked(false);
      }
      requestAnimationFrame(() => {
        if (nameRef.current) {
          nameRef.current.focus();
        }
      });
    },
    [enableUPDATEButtons, t]
  );

  const handleNewButtonClick = () => {
    if (hasChangesCreate || hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        setIsNewClicked(true);
        enableRegisterButtons();
        setSelectedFacility({ fc_id: null });
        setSelectedSiteId(null);
        setDisabledForm(false);
        setHasChangesUpdate(false);
        setFormValues((prev) => ({ ...prev, type: '221001' }));
        setResource('resource=speaker');
      });
    } else {
      tbRef.current?.deselectRow();
      emptyDetail();
      setIsNewClicked(true);
      enableRegisterButtons();
      setSelectedFacility({ fc_id: null });
      setSelectedSiteId(null);
      setDisabledForm(false);
      setHasChangesUpdate(false);
      setFormValues((prev) => ({ ...prev, type: '221001' }));
      setResource('resource=speaker');
    }
    requestAnimationFrame(() => {
      if (nameRef.current) {
        nameRef.current.focus();
      }
    });
  };

  const handleCancelButtonClick = () => {
    if (hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        setIsNewClicked(false);
        setDisabledForm(true);
        setHasChangesUpdate(false);
        setSelectedFacility({ fc_id: null });
        setSelectedSiteId(null);
        enableInitialButtons();

        requestAnimationFrame(() => {
          if (nameRef.current) {
            nameRef.current.focus();
          }
        });
      });
    } else if (isNewClicked) {
      if (hasChangesCreate) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), {
          mode: 'confirm',
        });
        message.confirmClicked().then(() => {
          emptyDetail();
          setIsNewClicked(false);
          setDisabledForm(true);
          enableInitialButtons();
          setHasChangesCreate(false);
          setSelectedSiteId(null);
        });
      } else {
        emptyDetail();
        setIsNewClicked(false);
        setDisabledForm(true);
        enableInitialButtons();
        setHasChangesCreate(false);
        setSelectedSiteId(null);
      }
    } else {
      reloadCallback();
    }
  };

  const handleRegistButtonClick = () => {
    const {
      description,
      facility_id: _facility_id,
      road_id,
      site_id,
      remote_terminal_unit_id,
      config_json,
      lat,
      lng,
      endpoint_id: _endpoint_id,
      ip: _ip,
      port: _port,
      ...fieldsToCheck
    } = formValues;

    const isEmptyField = Object.values(fieldsToCheck).some(
      (value) =>
        value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
    );
    if (isEmptyField || lat === null || lng === null) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      // TODO: i18n - hardcoded Korean string
      new NoticeMessage('필수 값을 모두 입력해주세요.');
      return;
    }

    const payload: CreateFacilityPayload = {
      site_id: !site_id || site_id === 'NO_MAPPING' ? null : Number(site_id),
      road_id: !road_id || road_id === 'NO_MAPPING' ? null : Number(road_id),
      remote_terminal_unit_id:
        !remote_terminal_unit_id || remote_terminal_unit_id === 'NO_MAPPING'
          ? null
          : Number(remote_terminal_unit_id),
      name: formValues.name ?? '',
      type: formValues.type ?? '',
      lat,
      lng,
      installed_date: formValues.installed_date ?? '',
      pic_name: formValues.pic_name ?? '',
      pic_phone_number: formValues.pic_phone_number ?? '',
      manufacturer: formValues.manufacturer ?? '',
      model: formValues.model ?? '',
      s_n: formValues.s_n ?? '',
      ip: formValues.ip ?? '',
      port: formValues.port ?? '',
      endpoint_id: formValues.endpoint_id || null,
      mac: formValues.mac ?? '',
      description: description ?? undefined,
      config_json: config_json ?? undefined,
    };
    createFacility(payload);
  };

  const handleConfirmButtonClick = () => {
    const {
      facility_id,
      description,
      road_id,
      site_id,
      remote_terminal_unit_id,
      config_json,
      lat,
      lng,
      endpoint_id: _endpoint_id,
      ip: _ip,
      port: _port,
      ...fieldsToCheck
    } = formValues;

    const isEmptyField = Object.values(fieldsToCheck).some(
      (value) =>
        value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
    );

    if (isEmptyField || lat === null || lng === null) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      // TODO: i18n - hardcoded Korean string
      new NoticeMessage('필수 값을 모두 입력해주세요.');
      return;
    }
    const payload: UpdateFacilityPayload = {
      facility_id: Number(facility_id),
      site_id: !site_id || site_id === 'NO_MAPPING' ? null : Number(site_id),
      road_id: !road_id || road_id === 'NO_MAPPING' ? null : Number(road_id),
      remote_terminal_unit_id:
        !remote_terminal_unit_id || remote_terminal_unit_id === 'NO_MAPPING'
          ? null
          : Number(remote_terminal_unit_id),
      name: formValues.name ?? '',
      type: formValues.type ?? '',
      lat,
      lng,
      installed_date: formValues.installed_date ?? '',
      pic_name: formValues.pic_name ?? '',
      pic_phone_number: formValues.pic_phone_number ?? '',
      manufacturer: formValues.manufacturer ?? '',
      model: formValues.model ?? '',
      s_n: formValues.s_n ?? '',
      ip: formValues.ip ?? '',
      port: formValues.port ?? '',
      endpoint_id: formValues.endpoint_id || null,
      mac: formValues.mac ?? '',
      description: description,
      config_json,
    };
    updateFacility({
      facilityData: payload,
      params: {
        facility_id: payload.facility_id,
      },
    });
  };

  const handleDeleteButtonClick = () => {
    if (dataFacility?.site_id || dataFacility?.road_id) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      // TODO: i18n - hardcoded Korean string
      new NoticeMessage('현재 시설물에 매핑된 사이트가 존재합니다. 먼저 매핑을 해제해주세요');
      return;
    }

    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const message = new NoticeMessage(t('msg > delete confirm'), {
      mode: 'confirm',
    });
    message.confirmClicked().then(() => {
      deleteFacility(selectedFacility?.fc_id ?? '');
    });
  };

  const roadOptions = selectedSiteId
    ? [
        // TODO: i18n - hardcoded Korean string
        { value: 'NO_MAPPING', label: '매핑 없음' },
        ...(dataSiteRoad?.sites
          ?.find((site) => site.site_id === Number(selectedSiteId))
          ?.roads.map((road) => ({
            value: road.road_id.toString(),
            label: `${road.name} (${road.road_id})`,
          })) || []),
      ]
    : [];

  const rtuOptions =
    selectedSiteId && selectedSiteId !== 'NO_MAPPING'
      ? [
          // TODO: i18n - hardcoded Korean string
          { value: 'NO_MAPPING', label: '매핑 없음' },
          ...(boxListData?.data?.rows
            ?.filter((box) => box.site_id === Number(selectedSiteId))
            .map((box) => ({
              value: box.remote_terminal_unit_id.toString(),
              label: `${box.name} (${box.remote_terminal_unit_id})`,
            })) || []),
        ]
      : [];

  useEffect(() => {
    if (dataFacility) {
      const data = detailFacility(dataFacility);
      setFormValues(data);
    }
  }, [dataFacility, detailFacility]);

  const logData = dataFacility
    ? [
        { label: t('cmn > registered by'), value: dataFacility.registered_by },
        {
          label: t('cmn > registered time'),
          value: dataFacility.registered_time
            ? formatFullDateTimeDDMMYYYY(new Date(dataFacility.registered_time))
            : dataFacility.registered_time,
        },
        { label: t('cmn > updated by'), value: dataFacility.updated_by },
        {
          label: t('cmn > updated time'),
          value: dataFacility.updated_time
            ? formatFullDateTimeDDMMYYYY(new Date(dataFacility.updated_time))
            : dataFacility.updated_time,
        },
      ]
    : [];

  return (
    <section className="wrap">
      <div className="header-title">
        {/* TODO: i18n - hardcoded Korean string */}
        <h3>시설물 관리</h3>
        <h3>&gt;</h3>
        <h3>스피커 속성 관리</h3>
      </div>

      <ContainerCard>
        {/* TODO: i18n - hardcoded Korean string */}
        <Filtering
          searchRef={searchRef}
          // labelSelect="유형"
          placeholder="명칭 / 시리얼 넘버"
          disableFiltering={true}
          onReset={handleReset}
          onSearch={handleSearch}
        >
          {/* <Select
            value={selectedOption}
            onChange={handleOnChangeInputSelect}
            options={
              commonListData?.['221']
                ? [
                  { value: 'All', label: t('cmn > all') },
                  ...(commonListData['221']?.code?.map((code, index) => ({
                    value: code ?? '',
                    label: commonListData['221']?.name[index] ?? '',
                  })) || []),
                ]
                : []
            }
          /> */}
        </Filtering>
      </ContainerCard>

      <ContainerCard>
        <ReactTabulator
          data={data}
          columns={facilityColumns}
          id="facility-management-table"
          layout={'fitColumns'}
          className="tabulator-custom w-full "
          onRef={(r) => {
            tbRef.current = r.current;
          }}
          options={optionsTabulator}
          events={{
            rowClick: (_e: UIEvent, row: RowComponent) => handleRowSelected(row),
            // Header click → delegate sorting to the server (see useServerTable).
            dataSorted: remoteSortHandler(table, FACILITY_SORT_FIELD_MAP),
            tableBuilt: () => {
              if (!tbRef.current) return;
              if (selectedFacility?.fc_id) {
                try {
                  const row = tbRef.current.getRow(selectedFacility?.fc_id) || null;
                  if (row && typeof row.select === 'function') {
                    row.select();
                    row.getElement().classList.add('custom-hover');
                  }
                } catch {
                  /* row not found */
                }
              } else if (newId) {
                try {
                  const row = tbRef.current.getRow(newId) || null;
                  if (row && typeof row.select === 'function') {
                    tbRef.current.scrollToRow(row, 'bottom', true);
                    row.select();
                    row.getElement().classList.add('custom-hover');
                    handleRowSelected(row);
                  }
                } catch {
                  /* row not found */
                }
              }
            },
          }}
        />
        <TablePagination
          total={pageData?.total ?? 0}
          page={table.page}
          size={table.size}
          totalPages={pageData?.total_pages ?? 0}
          onPageChange={table.setPage}
        />
      </ContainerCard>

      <ContainerCard className=" flex flex-col">
        <div className="box-management-col flex flex-col gap-3">
          <div className="grid grid-cols-3 gap-[50px]">
            {/* TODO: i18n - hardcoded Korean string */}
            <DetailForm
              className="items-center!"
              label="스피커 ID"
              disabled={true}
              onChange={handleInputChange}
              value={formValues.facility_id || ''}
              name={'facility_id'}
            />

            <DetailForm
              className="items-center!"
              label="명칭"
              formRef={nameRef}
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.name || ''}
              name={'name'}
              maxLength={100}
            />

            {/* <DetailForm
              inputType="select"
              className="items-center!"
              label="유형"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.type || ''}
              name={'type'}
              optionSelect={
                commonListData?.['221']
                  ? [
                    
                    ...(commonListData['221']?.code?.map((code, index) => ({
                      value: code ?? '',
                      label: commonListData['221']?.name[index] ?? '',
                    })) || []),
                  ]
                  : []
              }
            /> */}
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <div className="flex w-full flex-row gap-x-4">
              <DetailForm
                className="items-center!"
                label="설치 위/경도"
                required={true}
                showInput={false}
              >
                <div className="flex w-full flex-row gap-x-2">
                  <GeneralInput
                    customInput="w-full"
                    disabled={disabledForm}
                    onChange={handleInputChange}
                    value={formValues.lat || ''}
                    name={'lat'}
                    maxLength={100}
                    pattern={'^[0-9.]*$'}
                  />
                  <GeneralInput
                    customInput="w-full"
                    disabled={disabledForm}
                    onChange={handleInputChange}
                    value={formValues.lng || ''}
                    name={'lng'}
                    maxLength={100}
                    pattern={'^[0-9.]*$'}
                  />
                </div>
              </DetailForm>
            </div>

            <DetailForm
              className="items-center!"
              label="설치 일시"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.installed_date || ''}
              name={'installed_date'}
              readonly={true}
            />
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <DetailForm
              inputType="select"
              className="items-center!"
              label="매핑 사이트"
              optionSelect={
                formValues.type
                  ? [
                      { value: 'NO_MAPPING', label: '매핑 없음' },
                      ...(dataSiteRoad?.sites?.map((site) => ({
                        value: site.site_id.toString(),
                        label: `${site.name} (${site.site_id})`,
                      })) || []),
                    ]
                  : []
              }
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.site_id || ''}
              name={'site_id'}
            />
            <DetailForm
              inputType="select"
              className="items-center!"
              label="매핑 접근로"
              optionSelect={roadOptions}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.road_id || ''}
              name={'road_id'}
            />
            <DetailForm
              inputType="select"
              className="items-center!"
              label="매핑 함체"
              optionSelect={rtuOptions}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.remote_terminal_unit_id || ''}
              name={'remote_terminal_unit_id'}
            />
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <DetailForm
              className="items-center!"
              label="담당자"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.pic_name || ''}
              name={'pic_name'}
              maxLength={100}
            />

            <DetailForm
              className="items-center!"
              label="담당자 전화번호"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.pic_phone_number || ''}
              name={'pic_phone_number'}
              maxLength={100}
              pattern={'^[0-9]*$'}
            />
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <DetailForm
              className="items-center!"
              label="모델명"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.model || ''}
              name={'model'}
              maxLength={100}
            />

            <DetailForm
              className="items-center!"
              label="시리얼 넘버"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.s_n || ''}
              name={'s_n'}
              maxLength={100}
            />

            <DetailForm
              className="items-center!"
              label="제조사"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.manufacturer || ''}
              name={'manufacturer'}
              maxLength={100}
            />
          </div>

          <div className="grid grid-cols-3 gap-[50px]">
            <DetailForm
              className="items-center!"
              label="Mac Address"
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.mac || ''}
              name={'mac'}
              maxLength={100}
            />

            <DetailForm className="items-center!" label="ENDPOINT" showInput={false}>
              <ComboInput
                options={endpointOptions}
                value={formValues.endpoint_id || ''}
                disabled={disabledForm}
                name="endpoint_id"
                placeholder="입력 또는 선택"
                onChange={(val) => {
                  handleInputChange({
                    target: { name: 'endpoint_id', value: val },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
              />
            </DetailForm>

            {/* <div className="flex w-full flex-row gap-x-4">
              <DetailForm
                className="items-center!"
                label="IP / PORT"
                required={true}
                showInput={false}
              >
                <div className="flex w-full flex-row gap-x-2 items-center">
                  <GeneralInput
                    customInput="w-full"
                    disabled={disabledForm}
                    onChange={handleInputChange}
                    value={formValues.ip || ''}
                    name={'ip'}
                    maxLength={100}
                    pattern={'^[0-9.]*$'}
                  />
                  <span>:</span>
                  <GeneralInput
                    customInput="w-full"
                    disabled={disabledForm}
                    onChange={handleInputChange}
                    value={formValues.port || ''}
                    name={'port'}
                    maxLength={100}
                    pattern={'^[0-9.]*$'}
                  />
                </div>
              </DetailForm>
            </div> */}
          </div>

          <div className="grid grid-cols-1">
            <DetailForm
              className="h-[90px] [&>div:has(textarea)]:h-full [&>div:has(textarea)]:w-full [&_textarea]:h-full [&_textarea]:w-full"
              inputType="textarea"
              label="설명"
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.description || ''}
              name={'description'}
              maxLength={500}
            />
          </div>

          <div className="grid grid-cols-1">
            <DetailForm
              className="h-[180px] [&>div:has(textarea)]:h-full [&>div:has(textarea)]:w-full [&_textarea]:h-full [&_textarea]:w-full"
              inputType="textarea"
              label="json 속성 (dev)"
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.config_json || ''}
              name="config_json"
              maxLength={10000}
            />
          </div>

          <hr className="border-t border-gray-300" />
          <div className="flex items-center justify-between gap-4 w-full">
            <div className="flex-1">{disabledForm ? null : <LogList logs={logData} />}</div>
            <div className="flex-none">
              <ButtonGroup
                onClickDelete={handleDeleteButtonClick}
                onClickNew={handleNewButtonClick}
                onClickCancel={handleCancelButtonClick}
                onClickRegist={handleRegistButtonClick}
                onClickConfirm={handleConfirmButtonClick}
                isNewClicked={isNewClicked}
                cancelButtonState={buttonState.cancel}
                confirmButtonState={hasChangesUpdate ? false : buttonState.confirm}
                deleteButtonState={buttonState.delete}
                newButtonState={hasChangesCreate ? false : buttonState.create}
              />
            </div>
          </div>
        </div>
      </ContainerCard>
    </section>
  );
};

export default FacilityManagement;
